import axios from 'axios';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';


type Webhook = {
    created_at: string,
    description: string,
    livemode: boolean,
    metadata: null,
    status: string,
    type: null,
    updated_at: string,
    url: string,
    uuid: string,
}

export type WebhookEndpoint = {
    url: string,
    description: string,
    events: Array<string>,
}

export interface WebhookState {
    loaded: boolean;
    loading: Record<string, boolean>;
    webhooks: Webhook[]
    webhookEndpoints: WebhookEndpoint[],
    webhookConversations: Array<any>,
    webhook: Webhook | null,
    webhookEndpoint: WebhookEndpoint | null,
    webhookEndpointSecret: string,
    conversationsVersion: number,
}

const state: WebhookState = {
    loaded: false,
    loading: {
        webhooks: false,
        webhooksUpdate: false,
        webhooksDelete: false,
        webhookConversationResend: false,
        secret: false,
    },
    webhooks: [],
    webhook: null,
    webhookEndpoints: [],
    webhookEndpoint: null,
    webhookConversations: [],
    webhookEndpointSecret: '',
    conversationsVersion: 0,
};


const getters = {
    loaded: (state: WebhookState) => state.loaded,
    loading: (state: WebhookState) => state.loading,
    webhooks: (state: WebhookState) => state.webhooks,
    webhook: (state: WebhookState) => state.webhook,
    webhookEndpoint: (state: WebhookState) => state.webhookEndpoint,
    webhookEndpoints: (state: WebhookState) => state.webhookEndpoints,
    webhookConversations: (state: WebhookState) => state.webhookConversations,
    webhookEndpointSecret: (state: WebhookState) => state.webhookEndpointSecret,
    conversationsVersion: (state: WebhookState) => state.conversationsVersion,
};


const mutations = {
    [types.SET_WEBHOOKS_LOADING](state: WebhookState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },

    [types.SET_WEBHOOK_ENDPOINTS](state: WebhookState, webhookEndpoints: WebhookEndpoint[]) {
        state.webhookEndpoints = webhookEndpoints;
    },
    [types.SET_WEBHOOK_ENDPOINT](state: WebhookState, webhookEndpoint: WebhookEndpoint) {
        state.webhookEndpoint = webhookEndpoint;
    },

    [types.SET_WEBHOOKS_CONVERSATIONS](state: WebhookState, conversations: Array<any>) {
        state.webhookConversations = conversations;
    },
    [types.SET_WEBHOOK_ENDPOINT_SECRET](state: WebhookState, secret: string) {
        state.webhookEndpointSecret = secret;
    },
    [types.TOUCH_WEBHOOK_CONVERSATIONS](state: WebhookState) {
        state.conversationsVersion += 1;
    },
};

const actions = {

    /**
     * Get webhook endpoints
     */
    getWebhookEndpoints({commit}) {
        commit('SET_WEBHOOKS_LOADING', {subject: 'webhooks', loading: true});

        axios.get('/v1/webhook-endpoints')
            .then((response) => {
                commit('SET_WEBHOOK_ENDPOINTS', response?.data?.webhookEndpoints);
            }).catch((e) => {
            console.dir(e);
        }).finally(() => {
            commit('SET_WEBHOOKS_LOADING', {subject: 'webhooks', loading: false});
        });
    },


    /**
     * Get webhook endpoint
     */
    getWebhookEndpoint({commit}, webhook_uuid: string) {
        commit('SET_WEBHOOKS_LOADING', {subject: 'webhooks', loading: true});

        axios.get(`/v1/webhook-endpoints/${webhook_uuid}`)
            .then((response) => {
                commit('SET_WEBHOOK_ENDPOINT', response?.data?.webhookEndpoint);
            }).catch((e) => {
            console.dir(e);
        }).finally(() => {
            commit('SET_WEBHOOKS_LOADING', {subject: 'webhooks', loading: false});
        });
    },


    /**
     * Add webhook
     */
    async addWebhookEndpoint({commit}, data: WebhookEndpoint) {
        return await performApiRequest(commit, 'SET_WEBHOOKS_LOADING', 'webhooks', () => axios.post('/v1/webhook-endpoints', data));
    },


    /**
     * Update webhook
     */
    async updateWebhookEndpoint({commit}, webhook: Webhook): Promise<boolean> {
        commit('SET_WEBHOOKS_LOADING', {subject: 'webhooksUpdate', loading: true});

        try {
            await axios.put(`/v1/webhook-endpoints/${webhook.uuid}`, webhook);

            return true;
        } catch (e) {
            console.error('Error updating webhook endpoint:', e);

            return false;
        } finally {
            commit('SET_WEBHOOKS_LOADING', {subject: 'webhooksUpdate', loading: false});
        }
    },

    /**
     * Get webhook conversations
     */
    getWebhookConversations({commit}, uuid: string) {
        commit('SET_WEBHOOKS_LOADING', {subject: 'webhooks', loading: true});

        axios.get(`/v1/webhook-endpoints/${uuid}/conversations`)
            .then((response) => {
                commit('SET_WEBHOOKS_CONVERSATIONS', response?.data?.conversations);
            }).catch((e) => {
            console.dir(e);
        }).finally(() => {
            commit('SET_WEBHOOKS_LOADING', {subject: 'webhooks', loading: false});
        });
    },


    /**
     * Resend webhook conversations
     */
    resendWebhookConversation({commit}, conversation: any) {
        commit('SET_WEBHOOKS_LOADING', {subject: 'webhookConversationResend', loading: true});

        axios.post(`/v1/webhooks-conversations/${conversation.uuid}/retry`)
            .then(() => {
            }).catch((e) => {
            console.dir(e);
        }).finally(() => {
            commit('SET_WEBHOOKS_LOADING', {subject: 'webhookConversationResend', loading: false});
        });
    },


    /**
     * Delete webhook endpoint
     */
    async deleteWebhookEndpoint({commit}, webhook: Webhook): Promise<boolean> {
        commit('SET_WEBHOOKS_LOADING', {subject: 'webhooksDelete', loading: true});

        try {
            await axios.delete(`/v1/webhook-endpoints/${webhook.uuid}`);
            commit('SET_WEBHOOKS_CONVERSATIONS', []);

            return true;
        } catch (e) {
            console.error('Error deleting webhook endpoint:', e);

            return false;
        } finally {
            commit('SET_WEBHOOKS_LOADING', {subject: 'webhooksDelete', loading: false});
        }
    },


    /**
     * Fetch webhook endpoints signing secret value
     */
    async getWebhookEndpointSecret({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_WEBHOOKS_LOADING', 'secret', () => axios.get(`/v1/webhook-endpoints/${uuid}/secret`));
            commit('SET_WEBHOOK_ENDPOINT_SECRET', responseData.secret);
        } catch (error) {
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
